import React, { useState } from "react";
import CircularProgress from "component/misc/CircularProgress";
import { getSelectedLanguage } from "utils/language";
import { useTranslation } from "react-i18next";
import { reportService } from "../../services/reportService";

/**
 * Props:
 * - displayArticleId: number | string (required)
 * - onCustomizationApplied: function(customization) -> void (optional)
 * - onRewriteResult: legacy alias for onCustomizationApplied
 */
const CustomizeArticleReport = ({ displayArticleId, onRewriteResult, onCustomizationApplied }) => {
  const { t } = useTranslation();
  // Single-selects represented as a string; multi-selects as arrays
  const [articleDetail, setArticleDetail] = useState("original");
  const [writingStyle, setWritingStyle] = useState("original");
  const [perspective, setPerspective] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState("");

  const articleDetailOptions = ["short", "original", "detailed"];
  const writingStyleOptions = ["analytical", "original", "management"];
  const additionalInclusionOptions = [
    "quotation",
    "sources",
    "news",
    "graphs",
    "comparisons",
    "academical",
  ];


  // UI handlers
  const toggleOption = (option, setter, state) => {
      const exists = state.includes(option);
      const next = exists ? state.filter((i) => i !== option) : [...state, option];
      setter(next);
    };

  const selectOne = (option, setter) => {
      setter(option);
    };

  const normalizeDetail = (opt) => {
    const v = (opt || "").toLowerCase();
    if (v === "short") return "short";
    if (v === "detailed") return "detailed";
    return "original";
  };

  const normalizeStyle = (opt) => {
    const v = (opt || "").toLowerCase();
    if (v === "analytical") return "analytical";
    if (v === "management") return "management";
    return "original";
  };

  const normalizeAdditional = (vals = []) => {
    const mapping = {
      quotation: "quotation",
      sources: "sources",
      news: "news",
      graphs: "graphs",
      comparisons: "comparisons",
      academical: "academical",
    };
    return vals
      .map((v) => (v || "").toLowerCase().trim())
      .map((v) => mapping[v] || null)
      .filter(Boolean);
  };

  const handleApply = async () => {
    if (!displayArticleId) return;
    setSubmitting(true);
    setFeedback("");
    try {
      const payload = {
        detail_level: normalizeDetail(articleDetail),
        writing_style: normalizeStyle(writingStyle),
        additional_information: normalizeAdditional(perspective),
        language: getSelectedLanguage(),
      };
      const res = await reportService.createCustomization(displayArticleId, payload);
      setFeedback(t("reports.customize.applied"));
      if (res) {
        if (typeof onCustomizationApplied === "function") {
          onCustomizationApplied(res);
        }
        if (typeof onRewriteResult === "function") {
          onRewriteResult(res);
        }
      } 
    } catch (e) {
      setFeedback(e?.message || t("reports.customize.applyFailed"));
    } finally {
      setSubmitting(false);
    }
  };

 
  return (
    <div className="custom_panel_box position-relative">
      {/* Global overlay when applying changes */}
      {submitting && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
          style={{
            backdropFilter: "blur(4px)",
            background: "rgba(0,0,0,0.2)",
            zIndex: 5,
            borderRadius: "12px",
          }}
        >
          <CircularProgress size={28} />
          <span className="small text-light mt-2">{t("reports.customize.updatingReport")}</span>
        </div>
      )}

      {/* Customize Section */}
      <div className="custom-customize-box bg_glassEffect">
        <h6 className="custom-section-title fw-semibold py-2">
          {t("reports.customize.title")}
        </h6>

        {/* Article Details (single select) */}
        <div className="custom-group">
          <label className="custom-label">{t("reports.customize.reportDetails")}</label>
          <div className="custom_tab d-flex gap-2  justify-content-between">
            {articleDetailOptions.map((opt) => (
              <span
                key={opt}
                className={`custom-toggle w-100 mw-25 ${
                  articleDetail === opt ? "activetab" : ""
                }`}
                onClick={() => selectOne(opt, setArticleDetail)}
              >
                {t(`reports.customize.${opt}`)}
              </span>
            ))}
          </div>
        </div>

        {/* Writing Style (single select) */}
        <div className="custom-group pt-2">
          <label className="custom-label">{t("reports.customize.writingStyle")}</label>
          <div className="custom_tab d-flex gap-2  justify-content-between">
            {writingStyleOptions.map((opt) => (
              <span
                key={opt}
                className={`custom-toggle w-100 mw-25 ${
                  writingStyle === opt ? "activetab" : ""
                }`}
                onClick={() => selectOne(opt, setWritingStyle)}
              >
                {t(`reports.customize.${opt}`)}
              </span>
            ))}
          </div>
        </div>

        {/* Additional Inclusion (single-select) */}
        <div className="custom-group pt-2">
          <label className="custom-label pb-3">{t("reports.customize.additionalInclusion")}</label>
          <div className="self_togle_wrap row row-cols-1 row-cols-md-2 row-cols-xl-3 g-2">
            {additionalInclusionOptions.map((opt) => (
              <div className="col" key={opt}>
                <span
                  className={`custom-toggle w-100 d-block text-center ${perspective.includes(opt) ? "activetab" : ""
                    }`}
                  onClick={() => toggleOption(opt, setPerspective, perspective)} 
                >
                  {t(`reports.customize.${opt}`)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="d-flex justify-content-end mt-3">
          <button
            className="btn_enhance fw-semibold text-uppercase"
            onClick={handleApply}
            disabled={submitting}
          >
            {submitting ? t("reports.customize.applying") : t("reports.customize.update")}
          </button>
        </div>
        {feedback && <div className="small mt-2 text-light">{feedback}</div>}
      </div>
    </div>
  );
};

export default CustomizeArticleReport;
